import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import {
    Plus, KanbanSquare, Users, Building2, ExternalLink, Pencil, Trash2, Check,
    Briefcase, Code2, Palette, Megaphone, Wrench, Globe, Zap, Target,
    Shield, Rocket, Database, Cpu, Headphones, Star, BarChart2, ClipboardList,
    Calendar, FolderOpen, Layers, Package, Activity, FileText, ShoppingCart,
    Search, X as XIcon
} from "lucide-react";

const ENTITY_ICONS = [
    { key: "users", Icon: Users }, { key: "briefcase", Icon: Briefcase },
    { key: "code", Icon: Code2 }, { key: "palette", Icon: Palette },
    { key: "megaphone", Icon: Megaphone }, { key: "wrench", Icon: Wrench },
    { key: "globe", Icon: Globe }, { key: "zap", Icon: Zap },
    { key: "target", Icon: Target }, { key: "shield", Icon: Shield },
    { key: "rocket", Icon: Rocket }, { key: "database", Icon: Database },
    { key: "cpu", Icon: Cpu }, { key: "headphones", Icon: Headphones },
    { key: "star", Icon: Star }, { key: "bar_chart", Icon: BarChart2 },
    { key: "clipboard", Icon: ClipboardList }, { key: "calendar", Icon: Calendar },
    { key: "folder", Icon: FolderOpen }, { key: "layers", Icon: Layers },
    { key: "package", Icon: Package }, { key: "activity", Icon: Activity },
    { key: "kanban", Icon: KanbanSquare }, { key: "file_text", Icon: FileText },
    { key: "shopping_cart", Icon: ShoppingCart },
];

function getEntityIcon(iconKey, DefaultIcon, size = 15) {
    const found = ENTITY_ICONS.find(i => i.key === iconKey);
    const IconComp = (found?.Icon) || DefaultIcon;
    return <IconComp size={size} />;
}

function IconPicker({ value, onChange }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-2">Pictogramă</label>
            <div className="grid grid-cols-9 gap-1.5">
                {ENTITY_ICONS.map(({ key, Icon }) => (
                    <button key={key} type="button" onClick={() => onChange(key)}
                        data-testid={`icon-pick-${key}`}
                        className={`w-8 h-8 rounded-md flex items-center justify-center transition-all
                            ${value === key ? "bg-[#002FA7] text-white shadow-sm" : "bg-[#F8F9FA] text-[#6B7280] hover:bg-[#EEF2FF] hover:text-[#002FA7]"}`}>
                        <Icon size={15} />
                    </button>
                ))}
            </div>
        </div>
    );
}

const COLUMN_COLORS = { todo: "#6B7280", in_progress: "#3B82F6", review: "#F59E0B", done: "#10B981" };
const COLUMN_LABELS = { todo: "De făcut", in_progress: "În lucru", review: "Review", done: "Finalizat" };

export default function WorkspacePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = ["super_admin", "admin"].includes(user?.role);
    const canEditWorkspace = isAdmin; // editare / ștergere workspace = doar admin
    const [workspace, setWorkspace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCreateBoard, setShowCreateBoard] = useState(false);
    const [boardTemplates, setBoardTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [clearAssignees, setClearAssignees] = useState(false);
    const [showCreateTeam, setShowCreateTeam] = useState(false);
    const [showEditWs, setShowEditWs] = useState(false);
    const [boardForm, setBoardForm] = useState({ name: "", description: "", team_ids: [], icon: "kanban" });
    const [teamForm, setTeamForm] = useState({ name: "", description: "", icon: "users" });
    const [allUsers, setAllUsers] = useState([]);
    const [newTeamMemberIds, setNewTeamMemberIds] = useState([]);
    const [newTeamMemberSearch, setNewTeamMemberSearch] = useState("");
    const [editWsForm, setEditWsForm] = useState({ name: "", description: "" });
    const [saving, setSaving] = useState(false);

    // Team lead în acest workspace are aceleași drepturi ca un admin pentru creare echipe / board-uri / etc.
    const isWsLead = (workspace?.teams || []).some(t => t.is_led_by_me);
    const canManage = isAdmin || isWsLead; // creare echipe, board-uri, etc.

    // Filtre board-uri
    const [boardSearch, setBoardSearch] = useState("");
    const [boardTeamFilter, setBoardTeamFilter] = useState(""); // "" = toate echipele
    const [searchResults, setSearchResults] = useState({ boards: [], tasks: [] });
    const [searchLoading, setSearchLoading] = useState(false);

    // Debounced search: cautăm taskuri prin backend când query >= 2 caractere
    useEffect(() => {
        const q = boardSearch.trim();
        if (q.length < 2) { setSearchResults({ boards: [], tasks: [] }); return; }
        const handle = setTimeout(() => {
            setSearchLoading(true);
            API.get(`/workspaces/${id}/search`, { params: { q } })
                .then(r => setSearchResults(r.data))
                .catch(() => {})
                .finally(() => setSearchLoading(false));
        }, 350);
        return () => clearTimeout(handle);
    }, [boardSearch, id]);

    // Team lead poate crea board doar dacă conduce cel puțin o echipă în acest workspace
    const ledTeams = (workspace?.teams || []).filter(t => t.is_led_by_me);
    const canCreateBoard = isAdmin || ledTeams.length > 0;
    // Workspace-lead poate alege orice echipă din workspace când creează un board (la fel ca admin)
    const boardTeamOptions = (isAdmin || isWsLead) ? (workspace?.teams || []) : ledTeams;

    // Lista de board-uri filtrate
    const filteredBoards = useMemo(() => {
        const all = workspace?.boards || [];
        const q = boardSearch.trim().toLowerCase();
        return all.filter(b => {
            // Filter by team
            if (boardTeamFilter) {
                const tids = b.team_ids || (b.team_id ? [b.team_id] : []);
                if (!tids.includes(boardTeamFilter)) return false;
            }
            // Filter by search (name + description)
            if (q) {
                const hay = `${b.name || ""} ${b.description || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [workspace?.boards, boardSearch, boardTeamFilter]);

    const clearFilters = () => { setBoardSearch(""); setBoardTeamFilter(""); };
    const hasActiveFilter = boardSearch.trim().length > 0 || boardTeamFilter.length > 0;

    const fetchWorkspace = () => {
        setLoading(true);
        API.get(`/workspaces/${id}`)
            .then(r => {
                setWorkspace(r.data);
                setEditWsForm({ name: r.data.name, description: r.data.description || "" });
            })
            .catch(() => toast.error("Workspace negăsit"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchWorkspace(); }, [id]);

    useEffect(() => {
        if (showCreateBoard) {
            API.get("/board-templates").then(r => setBoardTemplates(r.data)).catch(() => setBoardTemplates([]));
        }
    }, [showCreateBoard]);

    // Încarcă utilizatorii când se deschide modalul „Echipă nouă" (pentru selecția inițială de membri)
    useEffect(() => {
        if (showCreateTeam && allUsers.length === 0) {
            API.get("/users").then(r => setAllUsers(r.data)).catch(() => {});
        }
    }, [showCreateTeam, allUsers.length]);

    const createBoard = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            let res;
            if (selectedTemplateId) {
                res = await API.post(`/board-templates/${selectedTemplateId}/create-board`, {
                    name: boardForm.name,
                    workspace_id: id,
                    team_id: boardForm.team_ids[0] || null,
                    clear_assignees: !!clearAssignees,
                });
            } else {
                res = await API.post("/boards", {
                    workspace_id: id,
                    team_ids: boardForm.team_ids,
                    name: boardForm.name,
                    description: boardForm.description,
                    icon: boardForm.icon,
                });
            }
            toast.success(selectedTemplateId ? "Board creat din șablon" : "Board creat");
            setShowCreateBoard(false);
            setBoardForm({ name: "", description: "", team_ids: [], icon: "kanban" });
            setSelectedTemplateId("");
            setClearAssignees(false);
            navigate(`/boards/${res.data.id}`);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const deleteBoardTemplate = async (tplId, e) => {
        e?.stopPropagation();
        if (!window.confirm("Ștergi acest șablon de board?")) return;
        try {
            await API.delete(`/board-templates/${tplId}`);
            toast.success("Șablon șters");
            setBoardTemplates(boardTemplates.filter(t => t.id !== tplId));
            if (selectedTemplateId === tplId) setSelectedTemplateId("");
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const createTeam = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await API.post("/teams", {
                workspace_id: id,
                name: teamForm.name,
                description: teamForm.description,
                icon: teamForm.icon,
            });
            const newTeamId = res.data?.id;
            // Adaugă membrii selectați (în bulk, dacă există)
            if (newTeamId && newTeamMemberIds.length > 0) {
                try {
                    await API.post(`/teams/${newTeamId}/members`, { user_ids: newTeamMemberIds });
                    toast.success(`Echipă creată cu ${newTeamMemberIds.length} ${newTeamMemberIds.length === 1 ? "membru" : "membri"}`);
                } catch (memErr) {
                    toast.success("Echipă creată");
                    toast.error("Membrii nu au putut fi adăugați: " + formatError(memErr));
                }
            } else {
                toast.success("Echipă creată");
            }
            setShowCreateTeam(false);
            setTeamForm({ name: "", description: "", icon: "users" });
            setNewTeamMemberIds([]);
            setNewTeamMemberSearch("");
            fetchWorkspace();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const saveWorkspace = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await API.patch(`/workspaces/${id}`, editWsForm);
            toast.success("Workspace actualizat");
            setShowEditWs(false);
            fetchWorkspace();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const deleteWorkspace = async () => {
        if (!window.confirm(`Ștergi workspace-ul "${workspace?.name}"? Această acțiune nu poate fi anulată.`)) return;
        try {
            await API.delete(`/workspaces/${id}`);
            toast.success("Workspace șters");
            navigate("/");
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    if (loading) return (
        <div className="p-6 space-y-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white border border-[#E4E4E7] rounded-md animate-pulse" />)}
        </div>
    );

    return (
        <div className="p-6 max-w-5xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {/* Header */}
            <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#002FA7] rounded-md flex items-center justify-center flex-shrink-0">
                        <Building2 size={20} className="text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                {workspace?.name}
                            </h1>
                            {canEditWorkspace && (
                                <div className="flex items-center gap-1">
                                    <button onClick={() => setShowEditWs(true)} data-testid="edit-workspace-btn"
                                        className="text-[#9CA3AF] hover:text-[#002FA7] p-1 rounded hover:bg-blue-50 transition-colors">
                                        <Pencil size={13} />
                                    </button>
                                    <button onClick={deleteWorkspace} data-testid="delete-workspace-btn"
                                        className="text-[#9CA3AF] hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors">
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            )}
                        </div>
                        {workspace?.description && <p className="text-sm text-[#9CA3AF]">{workspace.description}</p>}
                    </div>
                </div>
                {(canManage || canCreateBoard) && (
                    <div className="flex gap-2">
                        {canManage && (
                            <button onClick={() => setShowCreateTeam(true)} data-testid="create-team-btn"
                                className="flex items-center gap-1.5 text-xs border border-[#E4E4E7] text-[#4B5563] px-3 py-2 rounded-md hover:bg-[#F8F9FA] transition-colors">
                                <Plus size={14} /> Echipă nouă
                            </button>
                        )}
                        {canCreateBoard && (
                            <button onClick={() => setShowCreateBoard(true)} data-testid="create-board-btn"
                                className="flex items-center gap-1.5 text-xs bg-[#002FA7] text-white px-3 py-2 rounded-md hover:bg-[#002482] transition-colors">
                                <Plus size={14} /> Board nou
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Teams */}
            <div className="mb-8">
                <h2 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-widest mb-3">Echipe ({workspace?.teams?.length || 0})</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(workspace?.teams || []).map(team => (
                        <button key={team.id} onClick={() => navigate(`/teams/${team.id}`)}
                            data-testid={`team-card-${team.id}`}
                            className="text-left p-4 bg-white border border-[#E4E4E7] rounded-md hover:border-[#002FA7]/30 hover:shadow-sm transition-all group">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-7 h-7 bg-blue-50 rounded flex items-center justify-center text-[#002FA7]">
                                    {getEntityIcon(team.icon, Users, 15)}
                                </div>
                                <span className="font-semibold text-sm text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{team.name}</span>
                            </div>
                            {team.description && <p className="text-xs text-[#9CA3AF] mb-2 line-clamp-2">{team.description}</p>}
                            <div className="flex items-center gap-1 text-xs text-[#9CA3AF]">
                                <Users size={11} />
                                <span>{team.member_count || 0} {team.member_count === 1 ? "membru" : "membri"}</span>
                            </div>
                        </button>
                    ))}
                    {(workspace?.teams || []).length === 0 && (
                        <div className="col-span-3 text-center py-8 text-sm text-[#9CA3AF] border border-dashed border-[#E4E4E7] rounded-md">
                            Nicio echipă.{canManage ? " Creează prima echipă." : ""}
                        </div>
                    )}
                </div>
            </div>

            {/* Boards */}
            <div>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h2 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-widest">
                        Board-uri {hasActiveFilter
                            ? <span className="text-[#002FA7] dark:text-[#6A91FF]">({filteredBoards.length}/{workspace?.boards?.length || 0})</span>
                            : <span>({workspace?.boards?.length || 0})</span>
                        }
                    </h2>
                </div>

                {/* Bara de filtre */}
                <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md" data-testid="board-filters">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
                        <input
                            type="text"
                            value={boardSearch}
                            onChange={e => setBoardSearch(e.target.value)}
                            placeholder="Caută board după nume sau descriere..."
                            data-testid="board-search-input"
                            className="w-full pl-8 pr-8 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#0F1317] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                        />
                        {boardSearch && (
                            <button onClick={() => setBoardSearch("")} data-testid="clear-board-search"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111827] p-0.5">
                                <XIcon size={13} />
                            </button>
                        )}
                    </div>
                    <select value={boardTeamFilter} onChange={e => setBoardTeamFilter(e.target.value)}
                        data-testid="board-team-filter"
                        className="px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#0F1317] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] min-w-[160px]">
                        <option value="">Toate echipele</option>
                        {(workspace?.teams || []).map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                    {hasActiveFilter && (
                        <button onClick={clearFilters} data-testid="clear-all-filters"
                            className="flex items-center gap-1 text-xs text-[#9CA3AF] hover:text-[#002FA7] px-2 py-1 transition-colors">
                            <XIcon size={12} /> Resetează filtrele
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredBoards.map(board => (
                        <button key={board.id} onClick={() => navigate(`/boards/${board.id}`)}
                            data-testid={`board-card-${board.id}`}
                            className="text-left p-4 bg-white border border-[#E4E4E7] rounded-md hover:border-[#002FA7]/30 hover:shadow-sm transition-all group">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-7 h-7 bg-indigo-50 rounded flex items-center justify-center text-[#002FA7]">
                                    {getEntityIcon(board.icon, KanbanSquare, 15)}
                                </div>
                                <span className="font-semibold text-sm text-[#111827] flex-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{board.name}</span>
                                <ExternalLink size={14} className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            {board.description && <p className="text-xs text-[#9CA3AF] mb-1.5 line-clamp-2">{board.description}</p>}
                            {/* Team labels */}
                            {board.team_ids && board.team_ids.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-1.5">
                                    {board.team_ids.slice(0, 2).map(tid => {
                                        const t = workspace.teams?.find(tm => tm.id === tid);
                                        return t ? (
                                            <span key={tid} className="text-xs bg-blue-50 text-[#002FA7] px-1.5 py-0.5 rounded-sm font-medium">{t.name}</span>
                                        ) : null;
                                    })}
                                    {board.team_ids.length > 2 && (
                                        <span className="text-xs text-[#9CA3AF]">+{board.team_ids.length - 2}</span>
                                    )}
                                </div>
                            )}
                            {board.task_counts && Object.keys(board.task_counts).length > 0 ? (
                                <div className="flex items-center gap-3" data-testid={`board-task-counts-${board.id}`}>
                                    {["todo", "in_progress", "review", "done"].filter(k => board.task_counts[k] !== undefined).map(key => (
                                        <span key={key} className="flex items-center gap-1 text-xs text-[#9CA3AF]" title={COLUMN_LABELS[key]}>
                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: COLUMN_COLORS[key] }} />
                                            {board.task_counts[key] || 0}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-xs text-[#9CA3AF]">{board.task_count || 0} taskuri</span>
                            )}
                        </button>
                    ))}
                    {filteredBoards.length === 0 && (
                        <div className="col-span-3 text-center py-8 text-sm text-[#9CA3AF] border border-dashed border-[#E4E4E7] rounded-md" data-testid="no-boards-placeholder">
                            {hasActiveFilter
                                ? <>Niciun board nu corespunde filtrelor. <button onClick={clearFilters} className="text-[#002FA7] font-semibold hover:underline">Resetează filtrele</button>.</>
                                : <>Niciun board.{canManage ? " Creează primul board." : ""}</>
                            }
                        </div>
                    )}
                </div>

                {/* Search results: task-uri găsite */}
                {boardSearch.trim().length >= 2 && (
                    <div className="mt-6" data-testid="task-search-results">
                        <h2 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-widest mb-3 flex items-center gap-2">
                            Task-uri găsite
                            {searchLoading && <span className="text-[10px] font-normal italic normal-case">(caut...)</span>}
                            {!searchLoading && <span className="text-[#002FA7] dark:text-[#6A91FF]">({searchResults.tasks.length})</span>}
                        </h2>
                        {searchResults.tasks.length === 0 && !searchLoading && (
                            <div className="text-center py-6 text-sm text-[#9CA3AF] border border-dashed border-[#E4E4E7] dark:border-[#252B33] rounded-md">
                                Niciun task cu „{boardSearch}" în acest workspace.
                            </div>
                        )}
                        {searchResults.tasks.length > 0 && (
                            <div className="space-y-1.5">
                                {searchResults.tasks.map(task => (
                                    <button key={task.id} onClick={() => navigate(`/boards/${task.board_id}?task=${task.id}`)}
                                        data-testid={`task-search-result-${task.id}`}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md hover:border-[#002FA7]/30 hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A] transition-all text-left group">
                                        <span className="text-[10px] font-mono font-bold text-[#9CA3AF] flex-shrink-0">#{task.task_number}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-[#111827] dark:text-[#F3F4F6] truncate">{task.title}</p>
                                            <p className="text-xs text-[#9CA3AF] truncate">
                                                în <span className="font-semibold">{task.board?.name || "Board"}</span>
                                                {task.priority && <> · <span className={`font-medium ${task.priority === "high" ? "text-red-500" : task.priority === "medium" ? "text-orange-500" : "text-[#9CA3AF]"}`}>{task.priority === "high" ? "Urgent" : task.priority === "medium" ? "Mediu" : "Scăzut"}</span></>}
                                                {task.due_date && <> · Termen: {new Date(task.due_date).toLocaleDateString("ro-RO", { day: "2-digit", month: "short" })}</>}
                                            </p>
                                        </div>
                                        {task.assignees && task.assignees.length > 0 && (
                                            <div className="flex items-center -space-x-1.5 flex-shrink-0">
                                                {task.assignees.slice(0, 3).map(a => (
                                                    <div key={a.id} className="w-6 h-6 rounded-full border-2 border-white dark:border-[#151A21] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: a.avatar || "#002FA7" }} title={a.name}>
                                                        <span className="text-white text-[10px] font-bold">{a.name?.[0]?.toUpperCase()}</span>
                                                    </div>
                                                ))}
                                                {task.assignees.length > 3 && (
                                                    <div className="w-6 h-6 rounded-full border-2 border-white dark:border-[#151A21] bg-[#9CA3AF] flex items-center justify-center flex-shrink-0">
                                                        <span className="text-white text-[9px] font-bold">+{task.assignees.length - 3}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <ExternalLink size={13} className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 flex-shrink-0" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Edit Workspace Modal */}
            {canEditWorkspace && showEditWs && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowEditWs(false)} />
                    <div className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Editează workspace</h2>
                        </div>
                        <form onSubmit={saveWorkspace} className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume *</label>
                                <input autoFocus data-testid="edit-ws-name" value={editWsForm.name}
                                    onChange={e => setEditWsForm({ ...editWsForm, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere</label>
                                <input data-testid="edit-ws-description" value={editWsForm.description}
                                    onChange={e => setEditWsForm({ ...editWsForm, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowEditWs(false)} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button data-testid="save-ws-btn" type="submit" disabled={saving}
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se salvează..." : "Salvează"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Board Modal */}
            {canCreateBoard && showCreateBoard && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreateBoard(false)} />
                    <div className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Board nou</h2>
                        </div>
                        <form onSubmit={createBoard} className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume *</label>
                                <input autoFocus data-testid="new-board-name" value={boardForm.name}
                                    onChange={e => setBoardForm({ ...boardForm, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                            </div>
                            {boardTemplates.length > 0 && (
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Folosește șablon (opțional)</label>
                                    <div className="space-y-1 max-h-32 overflow-y-auto rounded-md border border-[#E4E4E7] p-1">
                                        <button type="button" onClick={() => setSelectedTemplateId("")}
                                            data-testid="board-tpl-none"
                                            className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between ${selectedTemplateId === "" ? "bg-[#EEF2FF] text-[#002FA7] font-semibold" : "hover:bg-[#F8F9FA]"}`}>
                                            <span>Fără șablon (4 coloane standard + Stand By)</span>
                                            {selectedTemplateId === "" && <Check size={11} />}
                                        </button>
                                        {boardTemplates.map(t => (
                                            <div key={t.id}
                                                className={`group flex items-center gap-1 px-2 py-1.5 rounded text-xs cursor-pointer ${selectedTemplateId === t.id ? "bg-[#EEF2FF] text-[#002FA7]" : "hover:bg-[#F8F9FA]"}`}
                                                onClick={() => setSelectedTemplateId(t.id)}
                                                data-testid={`board-tpl-${t.id}`}>
                                                <div className="flex-1 min-w-0">
                                                    <div className={`font-semibold truncate ${selectedTemplateId === t.id ? "" : "text-[#111827]"}`}>{t.name}</div>
                                                    <div className="text-[10px] text-[#9CA3AF] truncate">{(t.columns?.length || 0)} coloane · {(t.tasks?.length || 0)} task-uri · {(t.labels?.length || 0)} etichete{t.description ? ` · ${t.description}` : ""}</div>
                                                </div>
                                                {selectedTemplateId === t.id && <Check size={11} className="flex-shrink-0" />}
                                                <button type="button" onClick={(e) => deleteBoardTemplate(t.id, e)}
                                                    data-testid={`board-tpl-delete-${t.id}`}
                                                    className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-red-500 p-0.5 rounded transition-opacity">
                                                    <Trash2 size={11} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    {selectedTemplateId && (
                                        <label className="flex items-start gap-2 mt-2 cursor-pointer">
                                            <input type="checkbox"
                                                data-testid="tpl-clear-assignees"
                                                checked={clearAssignees}
                                                onChange={e => setClearAssignees(e.target.checked)}
                                                className="w-3.5 h-3.5 mt-0.5 accent-[#002FA7]" />
                                            <span className="text-xs text-[#4B5563] leading-tight">
                                                Curăță responsabilii
                                                <span className="block text-[10px] text-[#9CA3AF]">
                                                    Util când refolosești șablonul pe altă echipă. Task-urile vor fi create fără responsabili (le poți atribui după).
                                                </span>
                                            </span>
                                        </label>
                                    )}
                                </div>
                            )}
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Echipe {!isAdmin && ledTeams.length > 0 && <span className="text-[#9CA3AF] normal-case ml-1">(doar echipele pe care le conduci)</span>}</label>
                                {boardTeamOptions.length === 0 ? (
                                    <p className="text-xs text-[#9CA3AF] italic">Nicio echipă disponibilă</p>
                                ) : (
                                    <div className="space-y-1.5 max-h-28 overflow-y-auto rounded-md border border-[#E4E4E7] p-2">
                                        {boardTeamOptions.map(t => (
                                            <label key={t.id} className="flex items-center gap-2 cursor-pointer group">
                                                <input type="checkbox"
                                                    checked={boardForm.team_ids.includes(t.id)}
                                                    onChange={e => {
                                                        if (e.target.checked) {
                                                            setBoardForm({ ...boardForm, team_ids: [...boardForm.team_ids, t.id] });
                                                        } else {
                                                            setBoardForm({ ...boardForm, team_ids: boardForm.team_ids.filter(id => id !== t.id) });
                                                        }
                                                    }}
                                                    className="w-3.5 h-3.5 accent-[#002FA7]"
                                                    data-testid={`board-team-check-${t.id}`}
                                                />
                                                <span className="text-sm text-[#111827]">{t.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <IconPicker value={boardForm.icon} onChange={icon => setBoardForm({ ...boardForm, icon })} />
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowCreateBoard(false)} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button data-testid="create-board-submit" type="submit" disabled={saving}
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se creează..." : "Creează board"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Team Modal */}
            {canManage && showCreateTeam && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => { setShowCreateTeam(false); setNewTeamMemberIds([]); setNewTeamMemberSearch(""); }} />
                    <div className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10 flex flex-col max-h-[85vh]" data-testid="create-team-modal">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Echipă nouă</h2>
                        </div>
                        <form onSubmit={createTeam} className="flex flex-col flex-1 min-h-0">
                            <div className="px-5 py-4 space-y-4 overflow-y-auto">
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume *</label>
                                    <input autoFocus data-testid="new-team-name" value={teamForm.name}
                                        onChange={e => setTeamForm({ ...teamForm, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere</label>
                                    <input data-testid="new-team-description" value={teamForm.description}
                                        onChange={e => setTeamForm({ ...teamForm, description: e.target.value })}
                                        className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                                </div>
                                <IconPicker value={teamForm.icon} onChange={icon => setTeamForm({ ...teamForm, icon })} />
                                {/* Selecție membri inițiali */}
                                <div className="pt-2 border-t border-[#F3F4F6]">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide">Membri inițiali</label>
                                        <span className="text-xs text-[#6B7280]" data-testid="new-team-member-count">
                                            {newTeamMemberIds.length} selectat{newTeamMemberIds.length === 1 ? "" : "i"}
                                        </span>
                                    </div>
                                    <div className="relative mb-2">
                                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                                        <input
                                            data-testid="new-team-member-search"
                                            type="text"
                                            placeholder="Caută după nume sau email..."
                                            value={newTeamMemberSearch}
                                            onChange={e => setNewTeamMemberSearch(e.target.value)}
                                            className="w-full pl-8 pr-8 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                                        />
                                        {newTeamMemberSearch && (
                                            <button type="button" onClick={() => setNewTeamMemberSearch("")}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]">
                                                <XIcon size={14} />
                                            </button>
                                        )}
                                    </div>
                                    {(() => {
                                        const q = newTeamMemberSearch.trim().toLowerCase();
                                        const candidates = (allUsers || []).filter(u => u.status !== "deactivated");
                                        const filtered = candidates.filter(u => {
                                            if (!q) return true;
                                            return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                                        });
                                        if (candidates.length === 0) {
                                            return <div className="px-3 py-4 text-center text-xs text-[#6B7280] border border-dashed border-[#E4E4E7] rounded-md">Niciun utilizator disponibil</div>;
                                        }
                                        return (
                                            <div className="border border-[#E4E4E7] rounded-md max-h-56 overflow-y-auto" data-testid="new-team-member-list">
                                                {filtered.length === 0 ? (
                                                    <div className="px-3 py-4 text-center text-xs text-[#6B7280]">Niciun utilizator nu corespunde căutării</div>
                                                ) : filtered.map(u => {
                                                    const isSelected = newTeamMemberIds.includes(u.id);
                                                    return (
                                                        <label
                                                            key={u.id}
                                                            data-testid={`new-team-member-row-${u.id}`}
                                                            className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isSelected ? "bg-[#EEF2FF]" : "hover:bg-[#F8F9FA]"}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => setNewTeamMemberIds(prev => prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id])}
                                                                data-testid={`new-team-member-checkbox-${u.id}`}
                                                                className="w-4 h-4 accent-[#002FA7] cursor-pointer"
                                                            />
                                                            <div className="w-6 h-6 rounded-full bg-[#002FA7] text-white text-[10px] font-semibold flex items-center justify-center shrink-0">
                                                                {(u.name || u.email || "?").charAt(0).toUpperCase()}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium text-[#111827] truncate">{u.name || "(fără nume)"}</div>
                                                                <div className="text-[11px] text-[#6B7280] truncate">{u.email}</div>
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })()}
                                    <p className="text-[10px] text-[#9CA3AF] mt-1.5">Opțional — îi poți adăuga și mai târziu din pagina echipei.</p>
                                </div>
                            </div>
                            <div className="flex gap-2 px-5 py-4 border-t border-[#E4E4E7]">
                                <button type="button" onClick={() => { setShowCreateTeam(false); setNewTeamMemberIds([]); setNewTeamMemberSearch(""); }} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button data-testid="create-team-submit" type="submit" disabled={saving}
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se creează..." : "Creează echipă"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
